<template>
    <Card dis-hover class="attendance-graph-card">
        <p class="graph-title">Attendance Graph</p>
        <Tabs v-model="activeTab" @on-click="handleTabChange">
            <TabPane :label="isExternalData ? 'Pay Period' : 'Weekly'" name="weekly">
                <div v-if="!isLoading" class="graph-container">
                    <LineChart :chartdata-labels="weeklyLabels" :chartdata-sets="weeklyDataSets" />
                </div>
                <div v-else class="loading-container">
                    <Spin size="large"></Spin>
                    <p>Loading data...</p>
                </div>
            </TabPane>
            <TabPane v-if="!isExternalData" label="Monthly" name="monthly">
                <div v-if="!isLoading" class="graph-container">
                    <LineChart :chartdata-labels="monthlyLabels" :chartdata-sets="monthlyDataSets" />
                </div>
                <div v-else class="loading-container">
                    <Spin size="large"></Spin>
                    <p>Loading monthly data...</p>
                </div>
            </TabPane>
        </Tabs>
    </Card>
</template>

<script>
import moment from 'moment';
import LineChart from '@/components/Graphs/LineChart';
import { getPaginatedAttendanceRecords } from '@/services/attendancelogs.services';

export default {
    name: 'AttendanceGraph',
    components: { LineChart },
    props: {
        attendanceLogsList: {
            type: Array,
            default: () => [],
        },
        selectedDates: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            activeTab: 'weekly',
            isLoading: false,
            attendanceLogs: [],
            userId: localStorage.getItem('user_id'),
        };
    },
    computed: {
        isExternalData() {
            return this.attendanceLogsList && this.attendanceLogsList.length > 0;
        },
        currentLogs() {
            return this.isExternalData ? this.attendanceLogsList : this.attendanceLogs;
        },
        weeklyLabels() {
            if (this.isExternalData && this.selectedDates) {
                return this.generateLabels(this.selectedDates.from, this.selectedDates.to);
            }
            const labels = [];
            for (let i = 6; i >= 0; i--) {
                labels.push(moment().subtract(i, 'days').format('DD-MMM'));
            }
            return labels;
        },
        weeklyDataSets() {
            const data =
                this.isExternalData && this.selectedDates
                    ? this.processDataExternal(this.selectedDates.from, this.selectedDates.to)
                    : this.processData(7);
            return this.getChartDataSets(data);
        },
        monthlyLabels() {
            const labels = [];
            for (let i = 29; i >= 0; i--) {
                labels.push(moment().subtract(i, 'days').format('DD-MMM'));
            }
            return labels;
        },
        monthlyDataSets() {
            const data = this.processData(30);
            return this.getChartDataSets(data);
        },
    },
    watch: {
        activeTab() {
            // Force re-render of chart when tab changes if needed
            // Actually vue-chartjs should handle it via props but just in case
        },
    },
    mounted() {
        if (!this.isExternalData) {
            this.fetchAttendanceData();
        }
    },
    methods: {
        handleTabChange(name) {
            this.activeTab = name;
        },
        fetchAttendanceData() {
            this.isLoading = true;
            // Fetch records for the last 30 days to cover both weekly and monthly
            const data = {
                clockInTime: {
                    $gte: moment().subtract(30, 'days').startOf('day').toISOString(),
                    $lte: moment().endOf('day').toISOString(),
                },
            };
            getPaginatedAttendanceRecords(this.userId, data, 1, 100)
                .then((res) => {
                    this.attendanceLogs = res.data.data || res.data || [];
                    console.log('Attendance logs fetched successfully:', this.attendanceLogs);
                    this.isLoading = false;
                })
                .catch((err) => {
                    console.error('Failed to fetch attendance data:', err);
                    this.$Message.error('Failed to fetch attendance data');
                    this.isLoading = false;
                });
        },
        generateLabels(from, to) {
            const labels = [];
            const start = moment(from);
            const end = moment(to);
            const diff = end.diff(start, 'days');
            for (let i = 0; i <= diff; i++) {
                labels.push(moment(from).add(i, 'days').format('DD-MMM'));
            }
            return labels;
        },
        processDataExternal(from, to) {
            const clockInHours = [];
            const elapsedTimes = [];
            const statuses = [];

            const start = moment(from);
            const end = moment(to);
            const diff = end.diff(start, 'days');

            for (let i = 0; i <= diff; i++) {
                const date = moment(from).add(i, 'days').format('YYYY-MM-DD');
                const log = this.currentLogs.find((l) => moment(l.clockInTime).utc().format('YYYY-MM-DD') === date);

                if (log) {
                    const m = moment(log.clockInTime).utc();
                    clockInHours.push(m.hours() + m.minutes() / 60);
                    elapsedTimes.push(log.totalDailyHrs || 0);
                    statuses.push(log.status ? log.status.toUpperCase() : 'regular');
                } else {
                    clockInHours.push(null);
                    elapsedTimes.push(0);
                    statuses.push('regular');
                }
            }
            return { clockInHours, elapsedTimes, statuses };
        },
        processData(days) {
            const clockInHours = [];
            const elapsedTimes = [];
            const statuses = [];

            for (let i = days - 1; i >= 0; i--) {
                const date = moment().subtract(i, 'days').format('YYYY-MM-DD');

                const log = this.currentLogs.find((l) => moment(l.clockInTime).format('YYYY-MM-DD') === date);

                if (log) {
                    const m = moment(log.clockInTime).utc();
                    clockInHours.push(m.hours() + m.minutes() / 60);
                    elapsedTimes.push(log.totalDailyHrs || 0);

                    statuses.push(log.status ? log.status.toUpperCase() : 'regular');
                } else {
                    clockInHours.push(null);
                    elapsedTimes.push(0);
                    statuses.push('regular');
                }
            }
            return { clockInHours, elapsedTimes, statuses };
        },
        getChartDataSets(processedData) {
            const { clockInHours, elapsedTimes, statuses } = processedData;
            return [
                {
                    label: 'Clock In Time',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    backgroundColor: 'transparent',
                    data: clockInHours,
                    yAxisID: 'y-axis-time',
                    type: 'line',
                    fill: false,
                    statuses: statuses,
                    pointBackgroundColor: statuses.map((s) =>
                        s === 'LATE' ? 'rgba(255, 99, 132, 1)' : 'rgba(75, 192, 192, 1)'
                    ),
                    pointRadius: statuses.map((s) => (s === 'LATE' ? 6 : 3)),
                },
                {
                    label: 'Elapsed Time (Hrs)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    data: elapsedTimes,
                    yAxisID: 'y-axis-hours',
                    type: 'bar',
                },
            ];
        },
    },
};
</script>

<style scoped>
.attendance-graph-card {
    margin-bottom: 0;
}
.graph-title {
    color: #463c6e;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 15px;
}
.graph-container {
    height: 400px;
    margin-top: 10px;
}
.loading-container {
    height: 400px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}
</style>
